// src/pages/BoxLabelPrint/BoxLabelPrint.jsx
import {
    Box,
    Button,
    Divider,
    Heading,
    HStack,
    Input,
    SimpleGrid,
    Text,
    Textarea,
    VStack,
} from "@chakra-ui/react";
import { useMemo, useRef, useState } from "react";
import { useTableColors } from "../../Utils";
import DlsLogo from "./DlsLogo";

export default function BoxLabelPrint() {
    const colors = useTableColors();

    const printRef = useRef(null);

    const [clientName, setClientName] = useState("");
    const [boxContent, setBoxContent] = useState("");

    const canPrint = useMemo(() => clientName.trim().length > 0, [clientName]);

    function toDataUrl(imgEl) {
        const c = document.createElement("canvas");
        const w = imgEl?.naturalWidth || imgEl?.width || 0;
        const h = imgEl?.naturalHeight || imgEl?.height || 0;
        if (!w || !h) throw new Error("Image not loaded");
        c.width = w;
        c.height = h;
        const ctx = c.getContext("2d");
        ctx.drawImage(imgEl, 0, 0, w, h);
        return c.toDataURL("image/png");
    }

    const handlePrintBrowser = () => {
        if (!printRef.current) return;

        // Clone the label DOM so we can swap the logo src safely
        const clone = printRef.current.cloneNode(true);

        // Find the img in the clone + original
        const cloneImg = clone.querySelector("img");
        const liveImg = printRef.current.querySelector("img");

        // Try to embed logo as base64 so it prints reliably
        if (cloneImg && liveImg) {
            try {
                const dataUrl = toDataUrl(liveImg);
                cloneImg.setAttribute("src", dataUrl);
            } catch (_) {
                // fallback: keep original src (may still work depending on asset/url timing)
            }
        }

        const printContent = clone.innerHTML;

        const w = window.open("", "_blank");
        if (!w) return;

        w.document.write(`
      <html>
        <head>
          <style>
            @page { size: 6in 4in landscape; margin: 0; }
            body {
              margin: 0;
              display: flex;
              justify-content: center;
              align-items: center;
              height: 100%;
              font-family: Arial, sans-serif;
            }
            .print-container {
              width: 6in;
              height: 4in;
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              border: 1px solid black;
              background-color: #f9f9f9;
            }
            img { max-width: 45%; }
          </style>
        </head>
        <body>
          <div class="print-container">${printContent}</div>
          <script>
            const imgs = Array.from(document.images || []);
            Promise.all(imgs.map(i => i.complete ? Promise.resolve() : new Promise(r => i.onload = r)))
              .then(() => { window.focus(); window.print(); window.close(); });
          </script>
        </body>
      </html>
    `);

        w.document.close();
    };

    return (
        <Box p={{ base: 4, md: 6 }} minH="70vh">
            <HStack justify="space-between" align="start" mb={5} flexWrap="wrap" gap={3}>
                <VStack align="start" spacing={1}>
                    <Heading size="lg">Box Label Printer</Heading>
                    <Text fontSize="sm" color={colors?.mutedText || "gray.500"}>
                        Label spec: 6in × 4in (landscape).
                    </Text>
                </VStack>
            </HStack>

            <Divider mb={5} />

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
                {/* Controls */}
                <Box
                    // bg={colors?.theadBg || "gray.50"}
                    p={5}
                    borderRadius={10}
                    borderWidth="1px"
                    borderColor={colors?.border || "gray.200"}
                >
                    <VStack align="stretch" spacing={4}>
                        <Box>
                            <Text fontSize="sm" mb={1} fontWeight="600">
                                Head Title
                            </Text>
                            <Input
                                placeholder="Type client name..."
                                value={clientName}
                                onChange={(e) => setClientName(e.target.value)}
                            />
                        </Box>

                        <Box>
                            <Text fontSize="sm" mb={1} fontWeight="600">
                                Short Title
                            </Text>
                            <Textarea
                                placeholder="Route or anything else to write"
                                value={boxContent}
                                onChange={(e) => setBoxContent(e.target.value)}
                                rows={4}
                                mb={0}
                                w="100%"
                                borderWidth={1}
                                borderColor={"#999"}
                            />
                            <Text mt={2} fontSize="xs" color={colors?.mutedText || "gray.500"}>
                                Keep it short so it fits well.
                            </Text>
                        </Box>

                        <HStack justify="space-between">
                            <Button variant="outline" colorScheme="red" onClick={() => setBoxContent("")} isDisabled={!boxContent.trim()}>
                                Clear notes
                            </Button>

                            <Button colorScheme="blue" onClick={handlePrintBrowser} isDisabled={!canPrint}>
                                Print
                            </Button>
                        </HStack>
                    </VStack>
                </Box>

                {/* Preview */}
                <Box
                    bg={colors?.theadBg || "gray.50"}
                    p={5}
                    borderRadius={10}
                    borderWidth="1px"
                    borderColor={colors?.border || "gray.200"}
                >
                    <VStack align="stretch" spacing={3}>
                        <HStack justify="space-between">
                            <Text fontWeight="700">Preview</Text>
                            <Text fontSize="xs" color={colors?.mutedText || "gray.500"}>
                                (Exact 6×4 area)
                            </Text>
                        </HStack>

                        <DlsLogo clientName={clientName || "—"} boxContent={boxContent} printRef={printRef} />
                    </VStack>
                </Box>
            </SimpleGrid>
        </Box>
    );
}
